import { EnumValue, UniqueId } from "../../Common/Util";
import { InventoryItemLight, ReleaseStockResult } from "./InventoryEntities";

export class MovementResponse {
    constructor({ id, moveType, createAt, status, items, assignment, destination, user, userId } = {}) {
        this.id = id;
        this.moveType = moveType?.toLowerCase();
        this.createAt = createAt;
        this.status = status?.toLowerCase();
        this.items = items ?? [];
        this.assignment = new Assigment(assignment);
        this.assignmentType = assignment?.assignmentType?.toLowerCase();
        this.user = user;
        this.userId = userId;
        this.destination = destination;
    }
    type() {
        return this.items[0]?.assignment ?? "";
    }
}

export class Assigment {
    constructor({ assignmentType, assignId, kitNumber } = {}) {
        this.assignmentType = assignmentType;
        this.assignId = assignId != null ? assignId.toString() : null;
        this.kitNumber = kitNumber;
    }
}

export class MovementItemResponse {
    constructor({ id, itemId, itemName, releaseId, lotNumber, quantity, confirmedQuantity, presentation, assignment, comment }) {
        this.id = id;
        this.itemId = itemId;
        this.itemName = itemName
        this.releaseId = releaseId;
        this.lotNumber = lotNumber;
        this.quantity = quantity;
        this.confirmedQuantity = confirmedQuantity;
        this.presentation = presentation;
        this.assignment = new Assigment(assignment);
        this.comment = comment;
    }

}

export class MoveRequest {
    constructor({ id, moveType, userId, items = [], assignment, destination, confirmationUserId, comment, status, originDispenseMovementId } = {}) {
        this.id = id ?? 0;
        this.moveType = moveType;
        this.userId = userId;
        this.confirmationUserId = confirmationUserId;
        this.items = items ?? [];
        this.assignment = new Assigment(assignment);
        this.destination = destination;
        this.comment = comment;
        this.status = status;
        this.originDispenseMovementId = originDispenseMovementId ?? null;
    }
}

export class MoveItemRequest {
    constructor({ id, item, presentation, release, quantity, confirmedQuantity, assignment, comment,
        originMoveItemId, partialQuantity, releaseTargetState, releaseCondition, dispensedKitQuantity } = {}) {
        this.id = id ?? 0;
        this.item = item != null ? new InventoryItemLight(item) : null;
        this.release = release != null ? new ReleaseStockResult(release) : null;
        this.presentation = presentation ?? null;
        this.quantity = quantity ?? 0;
        this.confirmedQuantity = confirmedQuantity ?? 0;
        this.assignment = assignment != null ? new Assigment(assignment) : null;
        this.uniqueId = UniqueId();
        this.comment = comment;
        this.originMoveItemId = originMoveItemId ?? null;
        this.partialQuantity = partialQuantity ?? null;
        this.releaseTargetState = releaseTargetState ?? null;
        this.releaseCondition = releaseCondition ?? null;
        this.dispensedKitQuantity = dispensedKitQuantity ?? null;
    }

    confirmQuantity(confirmed) {
        if (confirmed < 0 || confirmed > this.quantity)
            throw new Error("Cantidad de confirmación no válida")
        this.confirmedQuantity = confirmed;
    }
}

export function MovementStateLog({
    id = 0, executionDate = new Date(), userId, movementId, nextStatus, previousStatus = null, comment = null } = {}) {
    this.id = id;
    this.executionDate = executionDate;
    this.userId = userId;
    this.movementId = movementId;
    this.nextStatus = nextStatus;
    this.previousStatus = previousStatus;
    this.comment = comment;
}

export class MoveItemAssigment {
    constructor({ name, id = null }) {
        this.name = name;
        this.id = id;
    }
}

export const MoveTypes = {
    AddStock: new EnumValue(1, 'add_stock'),
    AdjustStock: new EnumValue(2, 'adjust_stock'),
    ProtocolDispense: new EnumValue(3, 'protocol_dispense'),
    ProtocolDispenseRequest: new EnumValue(4, 'protocol_dispense_request'),
    LaboratoryKitDispense: new EnumValue(5, 'laboratory_kit_dispense'),
    LaboratoryKitDispenseRequest: new EnumValue(6, 'laboratory_kit_request'),
    PharmacyDispense: new EnumValue(7, 'pharmacy_dispense'),
    PharmacyDispenseRequest: new EnumValue(8, 'pharmacy_dispense_request'),
    ProtocolReturn: new EnumValue(9, 'PROTOCOL_RETURN'),
    TryParse: (type) => {
        switch (type?.toLowerCase()) {
            default: return EnumValue.Unknown()
            case "add_stock": return MoveTypes.AddStock
            case "adjust_stock": return MoveTypes.AdjustStock
            case "protocol_dispense": return MoveTypes.ProtocolDispense
            case "protocol_request_dispense": return MoveTypes.ProtocolDispenseRequest
            case "laboratory_kit_dispense": return MoveTypes.LaboratoryKitDispense
            case "laboratory_kit_request": return MoveTypes.LaboratoryKitDispenseRequest
            case "pharmacy_dispense": return MoveTypes.PharmacyDispense
            case "pharmacy_dispense_request": return MoveTypes.PharmacyDispenseRequest
            case "protocol_return": return MoveTypes.ProtocolReturn
        }
    },
    ToArray: () => [MoveTypes.AddStock,
    MoveTypes.AdjustStock,
    MoveTypes.LaboratoryKitDispense,
    MoveTypes.LaboratoryKitDispenseRequest,
    MoveTypes.ProtocolDispense,
    MoveTypes.PharmacyDispense,
    MoveTypes.PharmacyDispenseRequest],
    IsEquivalent: (value, expected) => value?.toLowerCase() == expected?.Name.toLowerCase()
}


export const MoveStatus = {
    Pending: new EnumValue(1, "Pending"),
    Confirmed: new EnumValue(2, "Confirmed"),
    Completed: new EnumValue(3, "Completed"),
    Canceled: new EnumValue(4, "Canceled"),
    TryParse: (state) => {
        switch (state?.toLowerCase()) {
            default: return EnumValue.Unknown();
            case "pending": return MoveStatus.Pending;
            case "confirmed": return MoveStatus.Confirmed;
            case "completed": return MoveStatus.Completed;
            case "canceled": return MoveStatus.Canceled;
        }
    },
    ToArray: () => [MoveStatus.Pending, MoveStatus.Confirmed, MoveStatus.Completed, MoveStatus.Canceled]
};

export const DestinationType = {
    PharmacyPatient: new EnumValue(1, "PharmacyPatient"),
    ProtocolPatient: new EnumValue(2, "ProtocolPatient"),
    TryParse: (state) => {
        switch (state?.toLowerCase()) {
            default: return EnumValue.Unknown();
            case "pharmacypatient": return DestinationType.PharmacyPatient;
            case "protocolpatient": return DestinationType.ProtocolPatient;
        }
    },
}

export const Destination = {
    PharmacyPatient(medicalRecordNumber, patientVisitId = null) {
        if (medicalRecordNumber == null || medicalRecordNumber.trim() === '')
            throw new Error("Patient medical record number is required for PharmacyPatient destination type");

        return { destinationType: DestinationType.PharmacyPatient.Name, destinationId: medicalRecordNumber, patientVisitId: patientVisitId }
    },
    ProtocolPatient(medicalRecordNumber, patientVisitId) {
        if (medicalRecordNumber == null || medicalRecordNumber.trim() === '')
            throw new Error("Patient medical record number is required for ProtocolPatient destination type");
        if (patientVisitId == null || patientVisitId <= 0)
            throw new Error("Patient visit ID is required for ProtocolPatient destination type");

        return { destinationType: DestinationType.ProtocolPatient.Name, destinationId: medicalRecordNumber, patientVisitId: patientVisitId }
    }
}

export const MovementValidations = {
    ItemExpiresLessThanGap: (item, monthGap) => {
        const expirationGap = new Date(new Date().setMonth(new Date().getMonth() + monthGap));
        const isCommentEmpty = item.comment == null || item.comment.trim() == '';

        return (new Date(item.release.expirationDate) <= expirationGap
            && isCommentEmpty)
    }
}


